import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LogOut, User, Phone, Mail, Building2, Shield, Save, Loader2, Camera } from 'lucide-react';

export default function Settings() {
  const { user, activeUserId, signOut } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', company_name: '', email: '', whatsapp_number: '' });

  useEffect(() => {
    if (activeUserId) fetchProfile();
  }, [activeUserId]);

  const fetchProfile = async () => {
    const { data } = await supabase
      .from('recruiters')
      .select('*')
      .eq('id', activeUserId!)
      .single();
    setProfile(data);
    if (data) {
      setForm({
        name: data.name || '',
        company_name: data.company_name || '',
        email: data.email || '',
        whatsapp_number: data.whatsapp_number || '',
      });
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await supabase
        .from('recruiters')
        .update({
          name: form.name,
          company_name: form.company_name,
          email: form.email,
          whatsapp_number: form.whatsapp_number,
        })
        .eq('id', activeUserId!);
      setProfile({ ...profile, ...form });
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    if (!window.confirm('Are you sure you want to log out?')) return;
    await signOut();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-[3px] border-slate-200 border-t-slate-800 rounded-full" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-in fade-in" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div>
        <h1 className="text-3xl font-black text-slate-900 tracking-tight">Settings</h1>
        <p className="text-slate-400 mt-1 text-[15px]">Manage your recruiter profile</p>
      </div>

      {/* Profile Card */}
      <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm">
        <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-5">Profile Information</h2>

        {/* Avatar */}
        <div className="flex items-center gap-4 mb-6 pb-6 border-b border-slate-100">
          <div className="relative group">
            <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center text-white text-xl font-bold">
              {profile?.name ? profile.name.charAt(0).toUpperCase() : 'R'}
            </div>
            <div className="absolute inset-0 bg-black/40 rounded-2xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
              <Camera size={18} className="text-white" />
            </div>
          </div>
          <div>
            <p className="text-[15px] font-bold text-slate-900">{profile?.name || 'Recruiter'}</p>
            <p className="text-[12px] text-slate-400">{profile?.company_name || 'Company'}</p>
          </div>
        </div>

        {/* Editable fields */}
        <div className="space-y-4">
          {[
            { label: 'Full Name', key: 'name', icon: User, type: 'text' },
            { label: 'Company Name', key: 'company_name', icon: Building2, type: 'text' },
            { label: 'Email', key: 'email', icon: Mail, type: 'email' },
            { label: 'WhatsApp Number', key: 'whatsapp_number', icon: Phone, type: 'tel' },
          ].map(field => {
            const Icon = field.icon;
            return (
              <div key={field.key}>
                <label className="text-[12px] font-semibold text-slate-500 mb-1.5 block">{field.label}</label>
                <div className="relative">
                  <Icon size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type={field.type}
                    value={(form as any)[field.key]}
                    onChange={(e) => setForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                    className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] text-slate-800 font-medium focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-300 focus:bg-white transition-all"
                  />
                </div>
              </div>
            );
          })}

          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 bg-slate-950 text-white text-[13px] font-semibold py-2.5 rounded-xl hover:bg-slate-800 disabled:opacity-50 transition-colors mt-2"
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Security */}
      <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm">
        <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">Security</h2>
        <div className="flex items-center gap-3 text-[13px] text-slate-600">
          <Shield size={16} className="text-slate-400" />
          <span>Logged in as <strong className="text-slate-800">{activeUserId === user?.id ? (user?.email || user?.phone) : profile?.name + ' (Impersonated)'}</strong></span>
        </div>
      </div>

      {/* Verification Status */}
      <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm">
        <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">Account Status</h2>
        <div className="flex items-center justify-between">
          <p className="text-[13px] text-slate-600 font-medium">Verification</p>
          <span className={`px-3 py-1 rounded-full text-[11px] font-bold uppercase ${profile?.status === 'Verified' ? 'bg-emerald-50 text-emerald-700' :
              profile?.status === 'Blocked' ? 'bg-red-50 text-red-600' :
                'bg-amber-50 text-amber-600'
            }`}>
            {profile?.status || 'Pending'}
          </span>
        </div>
      </div>

      {/* Logout */}
      <button
        onClick={handleLogout}
        className="w-full flex items-center justify-center gap-2 p-3.5 rounded-2xl border border-red-200 bg-red-50 text-red-600 text-[13px] font-bold hover:bg-red-100 transition-colors"
      >
        <LogOut size={16} />
        Log Out
      </button>
    </div>
  );
}
